type ErrorCopy = { message: string; code: number };

const AUTH_ERROR_COPY = {
  NETWORK_ERROR: { message: "网络连接失败", code: 1001 },
  INVALID_RESPONSE: { message: "请求暂时失败", code: 1002 },
  REGISTRATION_UNAVAILABLE: { message: "注册服务暂时不可用", code: 1101 },
  TRY_AGAIN_LATER: { message: "请求过于频繁，请稍后再试", code: 1102 },
  VERIFICATION_FAILED: { message: "验证失败，请重新完成验证", code: 1103 },
  REGISTRATION_FAILED: { message: "注册失败，请检查填写内容后重试", code: 1104 },
  EMAIL_BINDING_FAILED: { message: "邮箱绑定失败，请检查验证码后重试", code: 1105 },
  VERIFICATION_CODE_SEND_FAILED: { message: "验证码发送失败", code: 1106 },
  INVALID_CREDENTIALS: { message: "账号或密码不正确", code: 1201 },
  LOGIN_UNAVAILABLE: { message: "登录暂时不可用", code: 1202 },
  BROWSER_REQUEST_FORBIDDEN: { message: "当前请求无法完成，请刷新页面后重试", code: 1301 },
  AUTH_SESSION_REQUIRED: { message: "登录状态已失效，请重新登录", code: 1302 },
  AUTH_SESSION_STALE: { message: "登录状态已失效，请重新登录", code: 1303 },
  ACCOUNT_NOT_ACTIVE: { message: "账号当前不可用", code: 1304 },
  CREDENTIAL_ACCOUNT_NOT_FOUND: { message: "未找到可修改密码的账号", code: 1305 },
  SIGN_OUT_FAILED: { message: "退出登录失败", code: 1306 },
  INVALID_RESET_TOKEN: { message: "重置链接无效或已过期", code: 1401 },
  CREDENTIAL_AUTHORITY_NOT_ENABLED: { message: "密码服务暂时不可用", code: 1402 },
  INVALID_PASSWORD: { message: "当前密码不正确", code: 1403 },
  CREDENTIAL_UPDATE_CONFLICT: { message: "密码更新冲突，请重新登录后再试", code: 1404 },
  PASSWORD_CHANGE_FAILED: { message: "修改密码失败", code: 1405 },
  PASSWORD_RECOVERY_FAILED: { message: "请求暂时失败，请稍后再试", code: 1499 },
  AUTHORIZATION_REQUEST_INVALID: { message: "授权请求无效或已过期", code: 1501 },
  AUTHORIZATION_FAILED: { message: "授权请求失败", code: 1502 },
  AUTHORIZATION_RESPONSE_INVALID: { message: "授权响应无效", code: 1503 },
  SUBJECT_TOKEN_INVALID: { message: "授权凭据无效，请重新登录", code: 1504 },
  AUTH_USER_DISABLED: { message: "账号已停用，无法继续授权", code: 1505 },
  AUTHORIZATION_VERSION_STALE: { message: "账号权限已更新，请重新登录", code: 1506 },
  ACCESS_TOKEN_REQUIRED: { message: "授权凭据缺失，请重新登录", code: 1507 },
  ACCESS_TOKEN_INVALID: { message: "授权凭据无效，请重新登录", code: 1508 },
  ACCESS_TOKEN_EXPIRED: { message: "授权凭据已过期，请重新登录", code: 1509 },
  SERVICE_CLIENT_FORBIDDEN: { message: "当前服务无权执行此操作", code: 1510 },
  AUTHORIZATION_VERSION_MISSING: { message: "账号权限信息不完整，请重新登录", code: 1511 },
  DELEGATION_PERMISSION_FORBIDDEN: { message: "当前账号没有所需权限", code: 1512 },
  NOT_FOUND: { message: "请求的内容不存在", code: 1900 },
  INVALID_REQUEST: { message: "请求内容无效，请检查后重试", code: 1901 },
  INTERNAL_ERROR: { message: "服务暂时不可用，请稍后再试", code: 1902 },
  UNKNOWN_ERROR: { message: "请求暂时失败，请稍后再试", code: 1999 },
} as const satisfies Readonly<Record<string, ErrorCopy>>;

type AuthErrorKey = keyof typeof AUTH_ERROR_COPY;

function isAuthErrorKey(value: string): value is AuthErrorKey {
  return value in AUTH_ERROR_COPY;
}

function formatErrorCopy(copy: ErrorCopy) {
  return `${copy.message}（-${copy.code}）`;
}

const LOCALIZED_AUTH_ERRORS = new Set(Object.values(AUTH_ERROR_COPY).map(formatErrorCopy));
const VISIBLE_OAUTH_CLIENT_IDS = new Set(["gbc-web-panel", "gbc-foodari", "gbc-main"]);

export function localizedAuthError(
  error: unknown,
  fallback: AuthErrorKey = "UNKNOWN_ERROR",
) {
  const raw = error instanceof Error
    ? error.message.trim()
    : typeof error === "string"
      ? error.trim()
      : "";
  if (LOCALIZED_AUTH_ERRORS.has(raw)) return raw;
  const copy = isAuthErrorKey(raw) ? AUTH_ERROR_COPY[raw] : AUTH_ERROR_COPY[fallback];
  return formatErrorCopy(copy);
}

export async function requestJson<T>(request: () => Promise<Response>): Promise<
  | { ok: true; data: T }
  | { ok: false; error: string }
> {
  try {
    const response = await request();
    let body: unknown;
    try {
      body = JSON.parse(await response.text());
    } catch {
      return { ok: false, error: localizedAuthError("INVALID_RESPONSE") };
    }
    if (!response.ok) {
      const error =
        typeof body === "object" && body !== null && "code" in body
          ? String((body as { code: unknown }).code)
          : typeof body === "object" && body !== null && "error" in body
            ? String((body as { error: unknown }).error)
            : typeof body === "object" && body !== null && "message" in body
              ? String((body as { message: unknown }).message)
              : localizedAuthError("INVALID_RESPONSE");
      return { ok: false, error };
    }
    return { ok: true, data: body as T };
  } catch {
    return { ok: false, error: localizedAuthError("NETWORK_ERROR") };
  }
}

export function emailSignInBody(email: string, password: string, _oauthQuery?: string) {
  return { email, password };
}

export function registrationStatusMessage(status?: string) {
  if (status === "active") return "注册成功，请登录";
  if (status === "disabled") return "注册失败，请检查邮箱或展示昵称后重试";
  return "主站档案正在创建，请稍后重试";
}

export async function loadTrustedOAuthClient(input: {
  issuer: string;
  oauthQuery: string;
  fetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}) {
  const clientId = new URLSearchParams(input.oauthQuery).get("client_id");
  if (!clientId) throw new Error("OAUTH_CLIENT_QUERY_INVALID");
  const response = await (input.fetch ?? fetch)(`${input.issuer}/oauth2/public-client-prelogin`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ client_id: clientId, oauth_query: input.oauthQuery }),
  });
  if (!response.ok) throw new Error("OAUTH_CLIENT_QUERY_INVALID");
  const body = await response.json() as Record<string, unknown>;
  if (
    body.client_id !== clientId ||
    typeof body.client_name !== "string" ||
    !body.client_name ||
    !VISIBLE_OAUTH_CLIENT_IDS.has(clientId)
  ) {
    throw new Error("OAUTH_CLIENT_METADATA_INVALID");
  }
  return { clientId, clientName: body.client_name };
}
